import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { RootState } from "@store";
import { refreshToken } from "@slices/ZettleAuth/ZettleAuth";
import dayjs from "dayjs";
import { Concert } from "@slices/PageState/PageState";
import objectSupport from 'dayjs/plugin/objectSupport';
import { ErrorCategory, ErrorSeverity, createError, logError } from '@services/errorHandling';

dayjs.extend(objectSupport);

export interface BaseQueryReturnType {
    purchases: Purchase[];
}

export interface Payment {
    type: string;
}

export interface Product {
    name: string;
    quantity: number;
    grossValue: number;
}

export interface Purchase {
    payments: Payment[];
    products: Product[];
    timestamp: string;
}

export interface PurchaseRecord {
    quantity: number;
    timestamp: string;
    product: string;
    amount: number;
    payment_method: PaymentType;
}

export interface FlattenedPurchaseRecord {
    id: number;
    year: string;
    month: string;
    product: string;
    payment_method: PaymentType;
    quantity: number;
    amount: number;
}

interface PurchaseQueryParams {
    year: number,
    concert: Concert,
}

export enum PaymentType {
    Card,
    Cash,
}

export const paymentTypeMap = new Map([
    ["IZETTLE_CARD", PaymentType.Card],
    ["IZETTLE_CASH", PaymentType.Cash],
]);

export const productMap = (product: string): string => {
    switch (product) {
        case "Cake":
        case "Juice":
        case "Wine donation":
        case "Mulled wine":
        case "Wine":
        case "Pimms":
        case "Sangria":
        case "Punch":
            return "Bar";
    }
    return product;
};

export const startMonthMap = new Map([
    [Concert.Autumn, 8],
    [Concert.Winter, 11],
    [Concert.Spring, 0],
    [Concert.Summer, 3],
    [Concert.Family, 6],
]);

export const endMonthMap = new Map([
    [Concert.Autumn, 10],
    [Concert.Winter, 11],
    [Concert.Spring, 2],
    [Concert.Summer, 5],
    [Concert.Family, 6],
]);

export const yearIncrementMap = new Map([
    [Concert.Autumn, 0],
    [Concert.Winter, 0],
    [Concert.Spring, 1],
    [Concert.Summer, 1],
    [Concert.Family, 1],
]);

/**
 * Helper function to create a composite key for grouping
 */
export const createGroupKey = (year: string, month: string, product: string, payment_method: PaymentType): string => {
    return `${year}|${month}|${product}|${payment_method}`;
};

/**
 * Transforms purchase API response into flattened, aggregated records.
 * Replaces Arquero with native JavaScript for CSP compliance (no eval).
 */
export const purchaseResponseTransform = (baseQueryReturnValue: BaseQueryReturnType): FlattenedPurchaseRecord[] => {
    if (baseQueryReturnValue["purchases"].length === 0) return [];

    // Step 1: Flatten purchases into individual product records
    const flattenedPurchases: PurchaseRecord[] = [];

    baseQueryReturnValue["purchases"].forEach((purchase: Purchase) => {
        const payment = purchase["payments"][0];
        purchase["products"].forEach((product) => {
            // Convert to numbers at the source - API may return strings
            const quantity = typeof product.quantity === 'string' ? parseInt(product.quantity, 10) : product.quantity;
            const amount = typeof product.grossValue === 'string' ? parseFloat(product.grossValue) : product.grossValue;

            const paymentMethod = paymentTypeMap.get(payment.type);
            if (!paymentMethod) {
                console.warn(`Unknown payment type: ${payment.type}, defaulting to Cash`);
            }

            flattenedPurchases.push({
                quantity: quantity,
                timestamp: dayjs(purchase.timestamp).toJSON(),
                product: productMap(product.name),
                amount: amount,
                payment_method: paymentMethod ?? PaymentType.Cash,
            });
        });
    });

    if (process.env.NODE_ENV === 'development') {
        console.log(`Processing ${flattenedPurchases.length} purchase records`);
        if (flattenedPurchases.length > 0) {
            console.log('Sample flattened purchase:', flattenedPurchases[0]);
        }
    }

    // Step 2: Transform records - extract year/month
    interface TransformedRecord {
        year: string;
        month: string;
        product: string;
        payment_method: PaymentType;
        quantity: number;
        amount: number;
    }

    const transformedRecords: TransformedRecord[] = flattenedPurchases.map((record) => {
        const date = dayjs(record.timestamp);

        // Ensure proper type conversion
        const quantity = typeof record.quantity === 'number' ? record.quantity : parseInt(String(record.quantity), 10);
        const amount = typeof record.amount === 'number' ? record.amount : parseFloat(String(record.amount));

        if (isNaN(quantity) || isNaN(amount)) {
            const nanError = createError(
                ErrorCategory.VALIDATION,
                ErrorSeverity.ERROR,
                'NaN detected in purchase record during transformation',
                new Error('Invalid numeric data in purchase'),
                {
                    rawQuantity: record.quantity,
                    rawAmount: record.amount,
                    parsedQuantity: quantity,
                    parsedAmount: amount,
                    product: record.product
                }
            );
            logError(nanError);
        }

        return {
            year: date.year().toString(),
            month: date.month().toString(),
            product: record.product,
            payment_method: record.payment_method,
            quantity: quantity,
            amount: amount,
        };
    });

    if (process.env.NODE_ENV === 'development' && transformedRecords.length > 0) {
        console.log('First transformed record:', transformedRecords[0]);
        console.log('Transformed record types:', {
            quantity: typeof transformedRecords[0].quantity,
            amount: typeof transformedRecords[0].amount,
            quantityValue: transformedRecords[0].quantity,
            amountValue: transformedRecords[0].amount
        });
    }

    // Step 3: Group by year, month, product, payment_method and aggregate
    const groupedData = new Map<string, { amount: number; quantity: number; year: string; month: string; product: string; payment_method: PaymentType }>();

    transformedRecords.forEach((record, index) => {
        const key = createGroupKey(record.year, record.month, record.product, record.payment_method);

        if (groupedData.has(key)) {
            const existing = groupedData.get(key);
            if (!existing) return; // TypeScript guard, should never happen since we checked has(key)
            // Use proper numeric addition - don't use || which treats 0 as falsy
            existing.amount = existing.amount + record.amount;
            existing.quantity = existing.quantity + record.quantity;

            if (process.env.NODE_ENV === 'development' && index === 1) {
                console.log('Adding to existing group:', {
                    key,
                    existingBefore: { amount: existing.amount - record.amount, quantity: existing.quantity - record.quantity },
                    adding: { amount: record.amount, quantity: record.quantity },
                    existingAfter: { amount: existing.amount, quantity: existing.quantity }
                });
            }
        } else {
            groupedData.set(key, {
                year: record.year,
                month: record.month,
                product: record.product,
                payment_method: record.payment_method,
                amount: record.amount,
                quantity: record.quantity,
            });

            if (process.env.NODE_ENV === 'development' && index === 0) {
                console.log('Creating new group:', {
                    key,
                    values: { amount: record.amount, quantity: record.quantity }
                });
            }
        }
    });

    // Step 4: Convert Map to array and add row IDs
    const results: FlattenedPurchaseRecord[] = Array.from(groupedData.values()).map((record, index) => ({
        id: index + 1,
        year: record.year,
        month: record.month,
        product: record.product,
        payment_method: record.payment_method,
        quantity: record.quantity,
        amount: record.amount,
    }));

    if (process.env.NODE_ENV === 'development') {
        console.log(`Aggregated into ${results.length} grouped records`);
        if (results.length > 0) {
            console.log('Sample result:', results[0]);
            console.log('Result types:', {
                quantity: typeof results[0].quantity,
                amount: typeof results[0].amount,
                quantity_value: results[0].quantity,
                amount_value: results[0].amount
            });
        }
    }

    return results;
};

// Base query with automatic token refresh on 401
const baseQuery = fetchBaseQuery({
    baseUrl: "",
    prepareHeaders: (headers, { getState }) => {
        // By default, if we have a token in the store, let's use that for authenticated requests
        const token = (getState() as typeof RootState).zettleAuth?.token;
        if (token) {
            headers.set("authorization", `Bearer ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    // If we get a 401 error, try to refresh the token
    if (result.error && result.error.status === 401) {
        logError(createError(
            ErrorCategory.AUTH,
            ErrorSeverity.WARNING,
            'Access token expired, attempting refresh',
            result.error,
            { endpoint: typeof args === 'string' ? args : args.url }
        ));

        // Try to refresh the token
        const refreshResult = await api.dispatch(refreshToken());

        if (refreshToken.fulfilled.match(refreshResult)) {
            // Token refresh succeeded, retry the original request
            result = await baseQuery(args, api, extraOptions);
        } else {
            // Token refresh failed, user needs to re-authenticate
            logError(createError(
                ErrorCategory.AUTH,
                ErrorSeverity.ERROR,
                'Token refresh failed, user needs to log in again',
                undefined,
                { endpoint: typeof args === 'string' ? args : args.url }
            ));
        }
    }

    return result;
};

export const zettlePurchase = createApi({
    reducerPath: "zettlePurchase",
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getPurchaseByUUID: builder.query<object, string>({
            query: (uuid) => `/purchases/v2/${uuid}`,
        }),
        getPurchases: builder.query<object, PurchaseQueryParams>({
            query: ({year, concert}) => {
                const yearIncrement = yearIncrementMap.get(concert);
                const startMonth = startMonthMap.get(concert);
                const endMonth = endMonthMap.get(concert);

                if (yearIncrement === undefined || startMonth === undefined || endMonth === undefined) {
                    throw new Error(`Invalid concert type: ${concert}`);
                }

                const startDate = dayjs({
                    year: year + yearIncrement,
                    month: startMonth,
                    day: 1,
                    hour: 0,
                    minute: 0,
                });
                const endDate = startDate.month(endMonth).endOf('month');
                console.info("startDate", startDate.format())
                console.info("endDate", endDate.format())
                return { 
                    url: "/purchases/v2",
                    params: { 
                        startDate: startDate.format(),
                        endDate: endDate.format()
                    }
                }
            },
            transformResponse: purchaseResponseTransform,
        }),
    }),
});

export const { useGetPurchaseByUUIDQuery, useGetPurchasesQuery } = zettlePurchase;
